const BAR_COUNT = 64;
const PLAYED_COLOR = "#E94213";
const IDLE_COLOR = "#ffffff";

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const peaksFromChannelData = (data: Float32Array, barCount: number): number[] => {
  const blockSize = Math.floor(data.length / barCount) || 1;
  const peaks: number[] = [];
  for (let i = 0; i < barCount; i++) {
    const start = i * blockSize;
    let max = 0;
    for (let j = 0; j < blockSize && start + j < data.length; j++) {
      const v = Math.abs(data[start + j]);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  const peakMax = Math.max(...peaks, 0.01);
  return peaks.map((p) => p / peakMax);
};

const fallbackPeaks = (barCount: number, seed: number): number[] => {
  const peaks: number[] = [];
  let s = seed || 1;
  for (let i = 0; i < barCount; i++) {
    s = (s * 16807) % 2147483647;
    const t = i / barCount;
    const envelope = 0.35 + 0.65 * Math.sin(Math.PI * t);
    peaks.push(envelope * (0.25 + (s % 1000) / 1000));
  }
  return peaks;
};

const loadPeaks = async (url: string): Promise<{ peaks: number[]; duration: number }> => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const arrayBuffer = await res.arrayBuffer();
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    const channel = audioBuffer.getChannelData(0);
    const peaks = peaksFromChannelData(channel, BAR_COUNT);
    void ctx.close();
    return { peaks, duration: audioBuffer.duration };
  } catch {
    return { peaks: fallbackPeaks(BAR_COUNT, url.length), duration: 0 };
  }
};

const audioLabel = (card: HTMLElement, playing: boolean): string => {
  const name = card.getAttribute("data-audio-label")?.trim() ?? "";
  const verb = playing ? "Pause" : "Play";
  return name !== "" ? `${verb} audio from ${name}` : `${verb} audio`;
};

const setupTestimonials = () => {
  const cards = document.querySelectorAll<HTMLElement>("[data-audio-player]");
  if (cards.length === 0) return;

  let activeAudio: HTMLAudioElement | null = null;
  let activeSetPlaying: ((playing: boolean) => void) | null = null;
  let activeStopDraw: (() => void) | null = null;

  const pauseActive = () => {
    if (!activeAudio) return;
    activeAudio.pause();
    activeSetPlaying?.(false);
    activeStopDraw?.();
    activeAudio = null;
    activeSetPlaying = null;
    activeStopDraw = null;
  };

  cards.forEach((card) => {
    const audio = card.querySelector<HTMLAudioElement>("audio");
    const playBtn = card.querySelector<HTMLButtonElement>("[data-audio-play]");
    const toggleBtn = card.querySelector<HTMLButtonElement>("[data-audio-transcript-toggle]");
    const transcript = card.querySelector<HTMLElement>("[data-audio-transcript]");
    const canvas = card.querySelector<HTMLCanvasElement>("[data-audio-visualizer]");
    const timeCurrent = card.querySelector<HTMLElement>('[data-audio-time="current"]');
    const timeDuration = card.querySelector<HTMLElement>('[data-audio-time="duration"]');

    let peaks: number[] = fallbackPeaks(BAR_COUNT, 1);
    let rafId = 0;

    const setPlaying = (playing: boolean) => {
      if (!playBtn) return;
      playBtn.setAttribute("aria-pressed", playing ? "true" : "false");
      playBtn.classList.toggle("is-playing", playing);
      playBtn.setAttribute("aria-label", audioLabel(card, playing));
    };

    const setTranscriptOpen = (open: boolean) => {
      if (!transcript || !toggleBtn) return;
      card.classList.toggle("testimonial--transcript-open", open);
      transcript.hidden = !open;
      toggleBtn.setAttribute("aria-expanded", open ? "true" : "false");
      toggleBtn.textContent = open ? "Close Transcript" : "View Transcript";
    };

    const resizeCanvas = () => {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (progress = 0) => {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      if (width <= 0 || height <= 0) return;

      ctx.clearRect(0, 0, width, height);

      const count = peaks.length;
      const gap = 1;
      const barWidth = Math.max(1, (width - gap * (count - 1)) / count);
      const midY = height / 2;
      const maxHalf = height * 0.45;

      const playedColor =
        getComputedStyle(canvas).getPropertyValue("--waveform-played").trim() || PLAYED_COLOR;

      for (let i = 0; i < count; i++) {
        const x = i * (barWidth + gap);
        const half = Math.max(1, peaks[i] * maxHalf);
        const played = i / count < progress;
        ctx.fillStyle = played ? playedColor : IDLE_COLOR;
        ctx.fillRect(x, midY - half, barWidth, half * 2);
      }
    };

    const updateTime = () => {
      if (!audio) return;
      if (timeCurrent) timeCurrent.textContent = formatTime(audio.currentTime);
      const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
      if (timeDuration && duration) timeDuration.textContent = formatTime(duration);
    };

    const stopDrawLoop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      draw(audio && audio.duration ? audio.currentTime / audio.duration : 0);
    };

    const startDrawLoop = () => {
      const tick = () => {
        if (!audio || audio.paused) return;
        const progress = audio.duration ? audio.currentTime / audio.duration : 0;
        draw(progress);
        updateTime();
        rafId = requestAnimationFrame(tick);
      };
      stopDrawLoop();
      rafId = requestAnimationFrame(tick);
    };

    if (canvas && audio?.src) {
      resizeCanvas();
      draw(0);
      void loadPeaks(audio.src).then(({ peaks: loaded, duration }) => {
        peaks = loaded;
        if (duration && timeDuration) {
          timeDuration.textContent = formatTime(duration);
        }
        resizeCanvas();
        draw(0);
      });

      audio.addEventListener("loadedmetadata", updateTime);
      window.addEventListener("resize", () => {
        resizeCanvas();
        const progress =
          audio && !audio.paused && audio.duration
            ? audio.currentTime / audio.duration
            : audio?.duration
              ? audio.currentTime / audio.duration
              : 0;
        draw(progress);
      });
    }

    playBtn?.addEventListener("click", async () => {
      if (!audio) return;
      if (audio.paused) {
        if (activeAudio && activeAudio !== audio) {
          pauseActive();
        }
        try {
          await audio.play();
          setPlaying(true);
          startDrawLoop();
          activeAudio = audio;
          activeSetPlaying = setPlaying;
          activeStopDraw = stopDrawLoop;
        } catch {
          setPlaying(false);
          stopDrawLoop();
          if (activeAudio === audio) {
            activeAudio = null;
            activeSetPlaying = null;
            activeStopDraw = null;
          }
        }
      } else {
        audio.pause();
        setPlaying(false);
        stopDrawLoop();
        if (activeAudio === audio) {
          activeAudio = null;
          activeSetPlaying = null;
          activeStopDraw = null;
        }
      }
    });

    audio?.addEventListener("ended", () => {
      setPlaying(false);
      audio.currentTime = 0;
      stopDrawLoop();
      draw(0);
      updateTime();
      if (activeAudio === audio) {
        activeAudio = null;
        activeSetPlaying = null;
        activeStopDraw = null;
      }
    });

    audio?.addEventListener("pause", () => {
      if (audio.ended) return;
      setPlaying(false);
      stopDrawLoop();
      if (activeAudio === audio) {
        activeAudio = null;
        activeSetPlaying = null;
        activeStopDraw = null;
      }
    });

    if (toggleBtn && transcript) {
      toggleBtn.addEventListener("click", () => {
        const open = !card.classList.contains("testimonial--transcript-open");
        setTranscriptOpen(open);
      });
    }
  });
};

setupTestimonials();
